import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Select, FormControl, InputLabel, SelectChangeEvent,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/client';

interface Agent {
  agent_id: string;
  agent_name: string;
  description?: string;
  status: string;
  primary_language: string;
  created_at: string;
}

interface AgentsResponse {
  agents: Agent[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

const VOICE_OPTIONS = [
  { value: 'en-US-Claire', label: 'Claire (English US)' },
  { value: 'en-US-Jordan', label: 'Jordan (English US)' },
  { value: 'en-GB-Ryan', label: 'Ryan (English UK)' },
  { value: 'en-AU-Natalie', label: 'Natalie (English AU)' },
  { value: 'es-ES-Pablo', label: 'Pablo (Spanish ES)' },
  { value: 'fr-FR-Denise', label: 'Denise (French FR)' },
  { value: 'ja-JP-Nanami', label: 'Nanami (Japanese)' },
];

function StatusBadge({ status }: { status: string }) {
  const s = status?.toLowerCase();
  if (s === 'active') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
        style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399' }}>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Active
      </span>
    );
  }
  if (s === 'paused') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
        style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24' }}>
        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />Paused
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ background: 'rgba(107,114,128,0.2)', color: '#9ca3af' }}>
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Draft'}
    </span>
  );
}

export const AgentList: React.FC = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [form, setForm] = useState({
    agent_name: '',
    primary_language: 'en-US',
    supported_languages: ['en-US'],
    voice_id: 'en-US-Claire',
    personality_prompt: '',
    greeting_message: 'Hello! How can I help you today?',
    fallback_message: 'Sorry, I did not understand. Could you repeat?',
  });

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const res = await api.get<AgentsResponse>('/api/v1/agents', { params: { page: p, limit: 12 } });
      setAgents(res.data.agents || []);
      setPage(res.data.pagination.page);
      setPages(res.data.pagination.pages);
    } catch {
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, []);

  const handleOpen = () => {
    setCreateError(null);
    setForm({
      agent_name: '',
      primary_language: 'en-US',
      supported_languages: ['en-US'],
      voice_id: 'en-US-Claire',
      personality_prompt: '',
      greeting_message: 'Hello! How can I help you today?',
      fallback_message: 'Sorry, I did not understand. Could you repeat?',
    });
    setOpen(true);
  };

  const onCreate = async () => {
    if (!form.agent_name.trim()) {
      setCreateError('Agent name is required.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await api.post('/api/v1/agents', form);
      setOpen(false);
      await load(page);
    } catch (err: any) {
      setCreateError(err?.response?.data?.message || 'Failed to create agent.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-8 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Agents</h1>
          <p className="text-gray-400 mt-1">Manage your AI phone agents.</p>
        </div>
        <button
          onClick={handleOpen}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all duration-150"
          style={{ background: '#6366f1' }}
        >
          + Create Agent
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Loading agents...</p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && agents.length === 0 && (
        <div
          className="rounded-xl p-12 text-center"
          style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="text-5xl mb-4">🤖</div>
          <h3 className="text-white font-semibold text-lg mb-2">No agents yet</h3>
          <p className="text-gray-400 text-sm mb-6">
            Create your first AI agent to start handling calls.
          </p>
          <button
            onClick={handleOpen}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white"
            style={{ background: '#6366f1' }}
          >
            + Create Agent
          </button>
        </div>
      )}

      {/* Card grid */}
      {!loading && agents.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
            {agents.map((agent) => (
              <div
                key={agent.agent_id}
                className="rounded-xl p-5 flex flex-col"
                style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                {/* Top row */}
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-white font-semibold text-base leading-tight flex-1 mr-3 truncate">
                    {agent.agent_name}
                  </h3>
                  <StatusBadge status={agent.status} />
                </div>

                {/* Language tag */}
                <span
                  className="inline-block mb-3 px-2 py-0.5 rounded text-xs font-medium self-start"
                  style={{ background: 'rgba(59,130,246,0.12)', color: '#60a5fa' }}
                >
                  {agent.primary_language}
                </span>

                {/* Description */}
                {agent.description && (
                  <p className="text-gray-400 text-sm mb-4 leading-relaxed line-clamp-2 flex-1">
                    {agent.description}
                  </p>
                )}
                {!agent.description && <div className="flex-1" />}

                {/* Actions */}
                <div className="flex gap-2 flex-wrap mt-2">
                  <button
                    onClick={() => navigate(`/agents/${agent.agent_id}/edit`)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                    style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.2)' }}
                  >
                    ✏️ Edit
                  </button>
                  <Link
                    to={`/agents/${agent.agent_id}/kb`}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors no-underline"
                    style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    📚 Knowledge Base
                  </Link>
                  <Link
                    to={`/agents/${agent.agent_id}/integrations`}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors no-underline"
                    style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    🔗 Integrations
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => load(p)}
                  className="w-8 h-8 rounded-lg text-sm font-medium transition-all"
                  style={{
                    background: p === page ? '#6366f1' : 'rgba(255,255,255,0.05)',
                    color: p === page ? '#fff' : '#9ca3af',
                    border: p === page ? '1px solid rgba(99,102,241,0.5)' : '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create Agent Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ style: { background: '#111118', border: '1px solid rgba(255,255,255,0.1)' } }}
      >
        <DialogTitle sx={{ color: '#fff', fontSize: '1.125rem', fontWeight: 600 }}>
          Create Agent
        </DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-4 mt-1">
            <TextField
              label="Agent Name"
              value={form.agent_name}
              onChange={(e) => setForm({ ...form, agent_name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Primary Language"
              value={form.primary_language}
              onChange={(e) => setForm({ ...form, primary_language: e.target.value, supported_languages: [e.target.value] })}
              fullWidth
              placeholder="e.g. en-US"
            />

            <FormControl fullWidth>
              <InputLabel>Voice</InputLabel>
              <Select
                value={form.voice_id}
                label="Voice"
                onChange={(e: SelectChangeEvent) => setForm({ ...form, voice_id: e.target.value })}
              >
                {VOICE_OPTIONS.map((v) => (
                  <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Personality Prompt"
              value={form.personality_prompt}
              onChange={(e) => setForm({ ...form, personality_prompt: e.target.value })}
              multiline
              rows={3}
              fullWidth
              placeholder="You are a helpful customer support agent for [Company]. Be friendly and professional..."
            />

            <TextField
              label="Greeting Message"
              value={form.greeting_message}
              onChange={(e) => setForm({ ...form, greeting_message: e.target.value })}
              fullWidth
            />

            <TextField
              label="Fallback Message"
              value={form.fallback_message}
              onChange={(e) => setForm({ ...form, fallback_message: e.target.value })}
              fullWidth
            />

            {createError && (
              <div className="text-sm text-red-400 px-3 py-2 rounded-lg"
                style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                {createError}
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setOpen(false)} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={onCreate} variant="contained" disabled={creating}>
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
